import { AsCookieAnalyticsConsent, FilterValue, formatFilterLabel } from "../types/types";

export const trackSearchOpen = ({action,source,}: {
    action: "launch" | "modify";
    source: "hero" | "peek";
}) => {
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) {
        return;
    }
    window.gtag("event", "qr_search_open", {
        search_action: action,
        search_source: source
    });
};

type FilterBucket =
    | "0"
    | "1-2"
    | "3-5"
    | "6-9"
    | "10-15"
    | "16+";

type ResultBucket =
    | "1-5"
    | "6-10"
    | "11-15"
    | "16-20"
    | "21-25"
    | "26-30"
    | "31-35"
    | "36-40"
    | "41-45"
    | "46-50"
    | "51+";

const getFilterBucket = (count: number): FilterBucket => {
    if (count <= 0) return "0";
    if (count <= 2) return "1-2";
    if (count <= 5) return "3-5";
    if (count <= 9) return "6-9";
    if (count <= 15) return "10-15";
    return "16+";
};
const getResultBucket = (count: number): ResultBucket => {
    if (count <= 5) return "1-5";
    if (count <= 10) return "6-10";
    if (count <= 15) return "11-15";
    if (count <= 20) return "16-20";
    if (count <= 25) return "21-25";
    if (count <= 30) return "26-30";
    if (count <= 35) return "31-35";
    if (count <= 40) return "36-40";
    if (count <= 45) return "41-45";
    if (count <= 50) return "46-50";
    return "51+";
};
type SearchWeekday =
    | "none"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";

const getSearchWeekday = (date?:string | Date):SearchWeekday => {
    if(!date) return "none";
    const parsedDate = date instanceof Date ? date : new Date(`${date}T12:00:00`);
    const day = parsedDate.getDay();

    const weekdays:SearchWeekday[] = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday"
    ];

    return weekdays[day];
};
export const trackSearchSubmit = ({searchSurface,hasCity,hasDate,searchDate,filterCount,service,resultCount,citySlug,}: {
    searchSurface: "mobile" | "desktop";
    hasCity: boolean;
    hasDate: boolean;
    searchDate?:string | Date;
    filterCount: number;
    service: "none" | "lunch" | "dinner";
    resultCount: number;
    citySlug: string;
}) => {
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) {
        return;
    }
    if (!Number.isFinite(resultCount) || resultCount <= 0) {
        return;
    }
    const filterBucket = getFilterBucket(filterCount);
    const resultBucket = getResultBucket(resultCount);
    const searchWeekday = getSearchWeekday(searchDate);
    window.gtag("event", "qr_search_submit", {
        search_surface: searchSurface,
        has_city: hasCity,
        has_date: hasDate,
        search_weekday:searchWeekday,
        filter_bucket: filterBucket,
        service,
        result_bucket: resultBucket,
        city_slug: citySlug
    });
};



export const trackHomeSectionClick = ({sectionKey,clickSource}: {
    sectionKey:string;
    clickSource:"title"|"show_all";
}) => {
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) return;
    if (!sectionKey) return;
    window.gtag("event","qr_home_section_click",{
        section_key:sectionKey,
        click_source:clickSource
    });
};


type ExtendedResultBucket =
    | "0-5"
    | "6-10"
    | "11-15"
    | "16-20";

const getExtendedResultBucket = (count:number):ExtendedResultBucket => {
    if (count <= 5) return "0-5";
    if (count <= 10) return "6-10";
    if (count <= 15) return "11-15";
    return "16-20";
};

export const trackExtendedSearchShowAll = ({
    sectionLabel,
    resultCount
}: {
    sectionLabel:string;
    resultCount:number;
}) => {
    if (!Number.isFinite(resultCount) || resultCount < 0) return;
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) return;
    if (!sectionLabel) return;
    window.gtag("event","qr_extended_search_show_all",{
        section_label:sectionLabel,
        result_bucket:getExtendedResultBucket(resultCount)
    });
};


export const trackZeroResultSearchClick = ({
    searchSurface,
    filterCount
}: {
    searchSurface:
        | "filter_bar_mobile"
        | "search_page_mobile"
        | "topbar_desktop";
    filterCount:number;
}) => {
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) return;

    const filterBucket = getFilterBucket(filterCount);


    window.gtag("event","qr_zero_result_search_click",{
        search_surface:searchSurface,
        filter_bucket:filterBucket
    });
};


export const trackSearchPopularFilterClick = ({
    filterName,
    action
}:{
    filterName:string;
    action:"select"|"remove";
}) => {
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) return;
    if(!filterName) return;


    window.gtag("event","qr_search_popular_filter_click",{
        filter_name:filterName,
        filter_action:action
    });
};

export const trackToolbarFilterClick = ({
    filterName,
    action
}:{
    filterName:string;
    action:"select"|"remove";
}) => {
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) return;
    if(!filterName) return;


    window.gtag("event","qr_toolbar_filter_click",{
        filter_name:filterName,
        filter_action:action
    });
};


export const trackSearchFiltersUsed = ({
    filters,
    searchSurface
}:{
    filters:FilterValue[];
    searchSurface:"mobile"|"desktop";
}) => {
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) return;

    filters.forEach((filter)=>{
        const filterName=formatFilterLabel(filter);

        if(!filterName) return;

        window.gtag("event","qr_search_filter_used",{
            filter_name:filterName,
            search_surface:searchSurface
        });
    });
};


export const trackRestoWebsiteClick = ({restaurantId}:{
    restaurantId:number;
}) => {
    if (!Number.isFinite(restaurantId) || restaurantId <= 0) return;
    if (!(AsCookieAnalyticsConsent() && typeof window.gtag === "function")) return;

    window.gtag("event","qr_resto_website_click",{
        restaurant_id:restaurantId
    });
};